import {
    useLayoutEffect,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type TextInputSelectionChangeEvent,
    type TextInputKeyPressEvent,
    Text as NativeText,
    type FocusEvent,
    type BlurEvent,
    ScrollView,
    TextInput,
    Platform,
    View
} from "react-native";
import type ICodeEditorProps from "./type";
import type {
    CodeEditorInsertTextConstantType,
    CodeEditorKeyEditConstantType,
    CodeEditorCursorConstantType,
    CodeEditorSelectionType,
    CodeEditorKeyEventType,
    CodeEditorTextAreaType,
    CodeEditorKeyEditType,
    CodeEditorCursorType
} from "./type";
import stylesheet, {
    CODE_EDITOR_TOOLBAR_ACTION,
    getCodeEditorColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    usePointerType
} from "../../helpers/pointer";
import type {
    Token
} from "../codeViewer/type";
import {
    Lock as LockIcon
} from "lucide-react-native";
import {
    getCodeActionsHitMargin,
    getCodeLanguageLabel,
    getCodeTokenColor
} from "../codeViewer/stylesheet";
import {
    splitTokensIntoLines,
    parseCode
} from "../codeViewer/parser";
import {
    MODAL_TAB_OWNER_DATA_KEY
} from "../modal/stylesheet";
import {
    CODE_EDITOR_ENCODING
} from "../codeViewer/constant";
import CodeCopyAction from "../codeViewer/internal/copyAction";
import SystemToolbar from "../systemToolbar";
import Chip from "../chip";
import Text from "../text";

const CodeEditorPairs: Record<string, string> = {
    "\"": "\"",
    "'": "'",
    "(": ")",
    "[": "]",
    "{": "}",
    "`": "`"
};

const CodeEditorQuotes = [
    "\"",
    "'",
    "`"
];

const CodeEditorClosers = [
    ")",
    "}",
    "]"
];

const CodeEditorLockIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <LockIcon
    color={customColor}
    size={size}
/>;

const getCodeEditorCursor = ({
    selection,
    value
}: CodeEditorCursorConstantType): CodeEditorCursorType => {
    const caret = Math.min(Math.max(selection.end, 0), value.length);

    const beforeCaret = value.substring(0, caret);

    return {
        column: caret - (beforeCaret.lastIndexOf("\n") + 1) + 1,
        lineIndex: beforeCaret.split("\n").length - 1
    };
};

const getCodeEditorKeyEdit = ({
    isTabReleased,
    selection,
    tabSize,
    value,
    key
}: CodeEditorKeyEditConstantType): CodeEditorKeyEditType | null => {
    const {
        start,
        end
    } = selection;

    const before = value.substring(0, start);

    const after = value.substring(end);

    const previousCharacter = before.charAt(before.length - 1);

    const nextCharacter = after.charAt(0);

    const indentation = " ".repeat(tabSize);

    const closingPair = CodeEditorPairs[key];

    if(key === "Tab") {
        if(isTabReleased) {
            return null;
        }

        if(start !== end) {
            const lineStart = before.lastIndexOf("\n") + 1;

            const lineEndOffset = after.indexOf("\n");

            const lineEnd = lineEndOffset === -1 ? value.length : end + lineEndOffset;

            const selectedLines = value.substring(lineStart, lineEnd).split("\n");

            const indentedLines = selectedLines.map((line) => `${indentation}${line}`);

            return {
                value: `${value.substring(0, lineStart)}${indentedLines.join("\n")}${value.substring(lineEnd)}`,
                selectionEnd: end + tabSize * selectedLines.length,
                cursor: start + tabSize
            };
        }

        return {
            value: `${before}${indentation}${after}`,
            cursor: start + tabSize
        };
    }

    if(closingPair) {
        if(start !== end) {
            return {
                value: `${before}${key}${value.substring(start, end)}${closingPair}${after}`,
                cursor: end + 1
            };
        }

        if(CodeEditorQuotes.includes(key) && nextCharacter === key) {
            return {
                cursor: start + 1,
                value
            };
        }

        if(CodeEditorQuotes.includes(key) && /[a-zA-Z0-9]/.test(previousCharacter)) {
            return null;
        }

        if(nextCharacter === "" || /[\s\]})]/.test(nextCharacter)) {
            return {
                value: `${before}${key}${closingPair}${after}`,
                cursor: start + 1
            };
        }

        return null;
    }

    if(CodeEditorClosers.includes(key) && start === end && nextCharacter === key) {
        return {
            cursor: start + 1,
            value
        };
    }

    if(key === "Backspace" && start === end && start > 0 && nextCharacter !== "" && CodeEditorPairs[previousCharacter] === nextCharacter) {
        return {
            value: `${before.slice(0, -1)}${after.slice(1)}`,
            cursor: start - 1
        };
    }

    if(key !== "Enter" || start !== end) {
        return null;
    }

    const currentIndentation = (before.split("\n").pop() ?? "").match(/^\s*/)?.[0] ?? "";

    if(previousCharacter === "{" && nextCharacter === "}") {
        return {
            value: `${before}\n${currentIndentation}${indentation}\n${currentIndentation}${after}`,
            cursor: start + 1 + currentIndentation.length + tabSize
        };
    }

    if(!currentIndentation.length) {
        return null;
    }

    return {
        value: `${before}\n${currentIndentation}${after}`,
        cursor: start + 1 + currentIndentation.length
    };
};

const insertCodeEditorText = ({
    nextValue,
    textArea,
    value
}: CodeEditorInsertTextConstantType): boolean => {
    if(Platform.OS !== "web" || typeof document === "undefined") {
        return false;
    }

    let prefix = 0;

    const minimumLength = Math.min(value.length, nextValue.length);

    while(prefix < minimumLength && value.charAt(prefix) === nextValue.charAt(prefix)) {
        prefix++;
    }

    let suffix = 0;

    while(suffix < minimumLength - prefix && value.charAt(value.length - 1 - suffix) === nextValue.charAt(nextValue.length - 1 - suffix)) {
        suffix++;
    }

    textArea.setSelectionRange(prefix, value.length - suffix);

    try {
        return document.execCommand("insertText", false, nextValue.substring(prefix, nextValue.length - suffix));
    } catch {
        return false;
    }
};

const CodeEditor: FC<ICodeEditorProps> = ({
    spreadBehaviour = "stretch",
    isLineNumberVisible = true,
    isToolbarVisible = false,
    isShowStatusBar = false,
    toolbarContainerStyle,
    isScrollable = false,
    placeholderTextColor,
    toolbarActions = [],
    accessibilityLabel,
    isDisabled = false,
    onSelectionChange,
    containerStyle,
    customLocalize,
    selectionColor,
    languageLabel,
    borderRadius,
    onChangeText,
    cursorColor,
    customTheme,
    tabSize = 4,
    onKeyPress,
    editable,
    language,
    readOnly,
    onFocus,
    onBlur,
    style,
    value,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const [
        selection,
        setSelection
    ] = useState<CodeEditorSelectionType>({
        start: 0,
        end: 0
    });

    const [
        isFocused,
        setIsFocused
    ] = useState<boolean>(false);

    const inputRef = useRef<TextInput>(null);

    const pendingCursorRef = useRef<number | null>(null);

    const pendingSelectionEndRef = useRef<number | null>(null);

    const isTabReleasedRef = useRef(false);

    const lines = useMemo(() => splitTokensIntoLines(parseCode(value, language)), [
        language,
        value
    ]);

    const isReadOnly = editable === false || readOnly === true;

    const isCurrentFocused = isFocused && !isDisabled;

    const cursor = getCodeEditorCursor({
        selection,
        value
    });

    const currentBorderRadius = borderRadius ?? radiuses.surface;

    const currentLanguageLabel = getCodeLanguageLabel({
        languageLabel,
        language
    });

    const hitMargin = getCodeActionsHitMargin({
        controlHeights,
        touchTargets,
        pointerType
    });

    const editorColors = getCodeEditorColors({
        isDisabled,
        isReadOnly,
        colors
    });

    const webDirectionProps = Platform.OS === "web" ? {
        dir: "ltr"
    } : {};

    const webInputProps = Platform.OS === "web" && !isReadOnly && !isDisabled ? {
        dataSet: {
            [MODAL_TAB_OWNER_DATA_KEY]: "true"
        }
    } : {};

    const {
        activeLineNumber: activeLineNumberDynamicStyle,
        gutterDivider: gutterDividerDynamicStyle,
        toolbarAction: toolbarActionDynamicStyle,
        activeLine: activeLineDynamicStyle,
        lineNumber: lineNumberDynamicStyle,
        container: containerDynamicStyle,
        statusBar: statusBarDynamicStyle,
        codeText: codeTextDynamicStyle,
        language: languageDynamicStyle,
        content: contentDynamicStyle,
        toolbar: toolbarDynamicStyle,
        editor: editorDynamicStyle,
        input: inputDynamicStyle,
        line: lineDynamicStyle,
        code: codeDynamicStyle
    } = useStyles({
        borderRadius: currentBorderRadius,
        isFocused: isCurrentFocused,
        userStyle: containerStyle,
        isLineNumberVisible,
        isToolbarVisible,
        isShowStatusBar,
        spreadBehaviour,
        shapeConfigs,
        editorColors,
        isScrollable,
        pointerType,
        shadowAlpha,
        elevations,
        isDisabled,
        isReadOnly,
        typography,
        hitMargin,
        iconSizes,
        borders,
        colors,
        spaces
    });

    useEffect(() => {
        if(!isDisabled) {
            return;
        }

        setIsFocused(false);
    }, [
        isDisabled
    ]);

    useLayoutEffect(() => {
        const pendingCursor = pendingCursorRef.current;

        if(pendingCursor === null) {
            return;
        }

        const pendingSelectionEnd = pendingSelectionEndRef.current ?? pendingCursor;

        pendingCursorRef.current = null;

        pendingSelectionEndRef.current = null;

        const textArea = inputRef.current as unknown as CodeEditorTextAreaType | null;

        if(!textArea) {
            return;
        }

        const nextCursor = Math.min(pendingCursor, value.length);

        const nextSelectionEnd = Math.min(pendingSelectionEnd, value.length);

        textArea.setSelectionRange(nextCursor, nextSelectionEnd);

        setSelection({
            end: nextSelectionEnd,
            start: nextCursor
        });
    }, [
        value
    ]);

    const onInputFocus = (event: FocusEvent) => {
        setIsFocused(true);

        onFocus?.(event);
    };

    const onInputBlur = (event: BlurEvent) => {
        setIsFocused(false);

        onBlur?.(event);
    };

    const onInputSelectionChange = (event: TextInputSelectionChangeEvent) => {
        setSelection(event.nativeEvent.selection);

        onSelectionChange?.(event);
    };

    const onInputKeyPress = (event: TextInputKeyPressEvent) => {
        onKeyPress?.(event);

        if(Platform.OS !== "web" || isReadOnly || isDisabled || event.isDefaultPrevented()) {
            return;
        }

        const nativeEvent: CodeEditorKeyEventType = event.nativeEvent;

        if(nativeEvent.isComposing) {
            return;
        }

        if(nativeEvent.key === "Escape") {
            isTabReleasedRef.current = true;

            return;
        }

        const isTabReleased = isTabReleasedRef.current;

        isTabReleasedRef.current = false;

        if(nativeEvent.ctrlKey || nativeEvent.metaKey || nativeEvent.altKey || (nativeEvent.shiftKey && nativeEvent.key === "Tab")) {
            return;
        }

        const textArea = inputRef.current as unknown as CodeEditorTextAreaType | null;

        if(!textArea) {
            return;
        }

        const keyEdit = getCodeEditorKeyEdit({
            selection: {
                start: textArea.selectionStart,
                end: textArea.selectionEnd
            },
            key: nativeEvent.key,
            isTabReleased,
            tabSize,
            value
        });

        if(!keyEdit) {
            return;
        }

        event.preventDefault();

        if(keyEdit.value === value) {
            textArea.setSelectionRange(keyEdit.cursor, keyEdit.cursor);

            setSelection({
                start: keyEdit.cursor,
                end: keyEdit.cursor
            });

            return;
        }

        pendingCursorRef.current = keyEdit.cursor;

        pendingSelectionEndRef.current = keyEdit.selectionEnd ?? null;

        if(insertCodeEditorText({
            nextValue: keyEdit.value,
            textArea,
            value
        })) {
            return;
        }

        onChangeText?.(keyEdit.value);
    };

    const renderReadOnlyChip = () => {
        if(!isReadOnly || isDisabled) {
            return null;
        }

        return <Chip
            customLocalize={customLocalize}
            title={localize("read-only")}
            icon={CodeEditorLockIcon}
            customTheme={customTheme}
            spreadBehaviour="free"
            isClosable={false}
            type="neutral"
            size="small"
        />;
    };

    const renderLanguage = () => {
        if(!currentLanguageLabel) {
            return null;
        }

        return <Text
            style={languageDynamicStyle}
            customTheme={customTheme}
            variant="labelSmallSize"
            numberOfLines={1}
            color="mid"
        >
            {currentLanguageLabel}
        </Text>;
    };

    const renderToolbar = () => {
        if(!isToolbarVisible) {
            return null;
        }

        return <View
            aria-label={localize("code-toolbar")}
            style={[
                toolbarContainerStyle,
                stylesheet.toolbar,
                toolbarDynamicStyle
            ]}
            role="toolbar"
        >
            {toolbarActions.map((action, index) => <SystemToolbar
                {...CODE_EDITOR_TOOLBAR_ACTION}
                {...action}
                key={`toolbar-action-${index}`}
                style={[
                    action.style,
                    toolbarActionDynamicStyle
                ]}
            />)}
            <View
                style={stylesheet.toolbarSpacer}
            />
            {renderReadOnlyChip()}
            {renderLanguage()}
            <CodeCopyAction
                inset={spaces.spacingXs + borders.line}
                borderRadius={currentBorderRadius}
                customLocalize={customLocalize}
                customTheme={customTheme}
                isDisabled={isDisabled}
                code={value}
            />
        </View>;
    };

    const renderTokens = (lineTokens: Array<Token>, lineIndex: number) => {
        if(isDisabled) {
            return lineTokens.map((token) => token.value).join("");
        }

        return lineTokens.map((token, tokenIndex) => <NativeText
            key={`token-${lineIndex}-${tokenIndex}`}
            style={{
                color: getCodeTokenColor({
                    type: token.type,
                    colors
                })
            }}
        >
            {token.value}
        </NativeText>);
    };

    const renderLineNumber = (lineIndex: number, isActive: boolean) => {
        if(!isLineNumberVisible) {
            return null;
        }

        return <NativeText
            style={[
                stylesheet.lineNumber,
                lineNumberDynamicStyle,
                isActive ? activeLineNumberDynamicStyle : undefined
            ]}
            selectable={false}
            numberOfLines={1}
        >
            {lineIndex + 1}
        </NativeText>;
    };

    const renderLine = (lineTokens: Array<Token>, lineIndex: number) => {
        const isActive = isCurrentFocused && lineIndex === cursor.lineIndex;

        return <View
            style={[
                stylesheet.line,
                lineDynamicStyle,
                isActive ? activeLineDynamicStyle : undefined
            ]}
            key={`line-${lineIndex}`}
        >
            {renderLineNumber(lineIndex, isActive)}
            <View
                style={[
                    stylesheet.code,
                    codeDynamicStyle
                ]}
            >
                <NativeText
                    {...webDirectionProps}
                    style={[
                        stylesheet.codeText,
                        codeTextDynamicStyle
                    ]}
                >
                    {renderTokens(lineTokens, lineIndex)}
                </NativeText>
            </View>
        </View>;
    };

    const renderInput = () => {
        return <TextInput
            {...props}
            {...webDirectionProps}
            {...webInputProps}
            selectionColor={selectionColor ?? (Platform.OS === "ios" ? colors.system.state.overlay.focus.default : undefined)}
            placeholderTextColor={placeholderTextColor ?? colors.content.text.low}
            cursorColor={cursorColor ?? colors.system.state.overlay.focus.default}
            accessibilityLabel={accessibilityLabel ?? localize("code-editor")}
            onSelectionChange={onInputSelectionChange}
            readOnly={isReadOnly || isDisabled}
            underlineColorAndroid="transparent"
            onKeyPress={onInputKeyPress}
            onChangeText={onChangeText}
            aria-disabled={isDisabled}
            onFocus={onInputFocus}
            style={[
                stylesheet.input,
                inputDynamicStyle
            ]}
            autoCapitalize="none"
            scrollEnabled={false}
            onBlur={onInputBlur}
            autoCorrect={false}
            autoComplete="off"
            spellCheck={false}
            multiline={true}
            ref={inputRef}
            value={value}
        />;
    };

    const renderEditorContent = () => {
        return <ScrollView
            {...webDirectionProps}
            showsHorizontalScrollIndicator={true}
            style={stylesheet.scroll}
            contentContainerStyle={[
                stylesheet.content,
                contentDynamicStyle
            ]}
            horizontal={true}
        >
            <View
                style={stylesheet.lines}
            >
                {
                    isLineNumberVisible ?
                        <View
                            style={[
                                stylesheet.gutterDivider,
                                gutterDividerDynamicStyle
                            ]}
                            aria-hidden={true}
                        />
                    :
                        null
                }
                <View
                    aria-hidden={true}
                >
                    {lines.map((lineTokens, lineIndex) => renderLine(lineTokens, lineIndex))}
                </View>
                {renderInput()}
            </View>
        </ScrollView>;
    };

    const renderEditor = () => {
        if(!isScrollable) {
            return <View
                style={[
                    style,
                    stylesheet.editor,
                    editorDynamicStyle
                ]}
            >
                {renderEditorContent()}
            </View>;
        }

        return <View
            style={[
                style,
                stylesheet.editor,
                editorDynamicStyle
            ]}
        >
            <ScrollView
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
            >
                {renderEditorContent()}
            </ScrollView>
        </View>;
    };

    const renderStatusBar = () => {
        if(!isShowStatusBar) {
            return null;
        }

        const statusItems = [
            localize("code-cursor-position", [
                cursor.lineIndex + 1,
                cursor.column
            ]),
            currentLanguageLabel,
            CODE_EDITOR_ENCODING,
            localize("code-indent-spaces", [
                tabSize
            ])
        ].filter((statusItem): statusItem is string => !!statusItem);

        return <View
            style={[
                stylesheet.statusBar,
                statusBarDynamicStyle
            ]}
        >
            {statusItems.map((statusItem, statusIndex) => <Text
                customColor={isDisabled ? editorColors.textColor : undefined}
                key={`status-item-${statusIndex}`}
                customTheme={customTheme}
                variant="bodySmallSize"
                color="low"
            >
                {statusItem}
            </Text>)}
        </View>;
    };

    return <View
        style={[
            containerStyle,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        {renderToolbar()}
        {renderEditor()}
        {renderStatusBar()}
    </View>;
};
export default CodeEditor;
